import React, { useState, useEffect } from 'react';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './components/Dashboard';
import ProjectUpload from './components/ProjectUpload';
import EstimateViewer from './components/EstimateViewer';
import RFIViewer from './components/RFIViewer';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import { createEstimate } from './services/estimator';

// ─── Inner app (rendered only when authenticated) ────────────────────────────
function AuthenticatedApp() {
  const { user, profile, signOut, currentPlan, planLimits, canCreateEstimate } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedProject, setSelectedProject] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [rfi, setRFI] = useState(null);

  // Show success/cancelled banners from Stripe redirect
  const [checkoutBanner, setCheckoutBanner] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      setCheckoutBanner({ type: 'success', msg: '🎉 Subscription activated! Welcome to ConstructPro.' });
    } else if (checkout === 'cancelled') {
      setCheckoutBanner({ type: 'warn', msg: 'Checkout cancelled. You can upgrade any time.' });
    }
    if (checkout) {
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGenerateEstimate = async (estimateData) => {
    if (!canCreateEstimate()) {
      alert('You\'ve reached your monthly estimate limit. Upgrade to Pro for unlimited estimates.');
      return;
    }

    try {
      console.log('🔧 Generating estimate with data:');
      console.log('  Project:', estimateData.project?.name);
      console.log('  Items:', estimateData.validatedItems?.length || 0);
      console.log('  Overhead:', (estimateData.overhead * 100).toFixed(0) + '%');
      console.log('  Profit:', (estimateData.profit * 100).toFixed(0) + '%');

      const result = await createEstimate(estimateData);

      console.log('✅ Estimate generated:');
      console.log('  ID:', result.id);
      console.log('  Total:', '$' + parseFloat(result.summary.estimateTotal).toLocaleString());
      console.log('  Line items:', result.lineItems.length);
      console.log('  Trades:', Object.keys(result.summary.byTrade).length);

      if (result && result.summary) {
        result.projectType = estimateData.projectType || 'office';
        result.region = estimateData.region || 'southeast';
        result.companyName = estimateData.companyName || '';
        result.companyAddress = estimateData.companyAddress || '';
        result.companyPhone = estimateData.companyPhone || '';
        result.companyEmail = estimateData.companyEmail || '';
        result.companyLogo = estimateData.companyLogo || null;
        result.showOverheadProfit = estimateData.showOverheadProfit || false;
        result.includeContingency = estimateData.includeContingency !== false;
        result.contingencyPercent = estimateData.contingencyPercent || 0.03;
        result.projectAddress = estimateData.project?.address || '';

        localStorage.setItem('lastEstimate', JSON.stringify(result));
        localStorage.setItem('lastProject', JSON.stringify(estimateData.project));

        setEstimate(result);
        setSelectedProject(estimateData.project);
        setCurrentPage('estimate');
      } else {
        alert('Estimate generated but missing summary data');
      }
    } catch (error) {
      console.error('❌ Error generating estimate:', error);
      alert('Error generating estimate: ' + error.message);
    }
  };

  const handleGenerateRFI = async (projectData) => {
    try {
      console.log('📋 Generating RFI for project:', projectData.project?.name);
      const rfiData = {
        id: 'RFI-' + Date.now(),
        createdDate: new Date().toISOString(),
        projectName: projectData.project?.name || 'Project',
        rfiItems: [
          {
            number: 1,
            category: 'Design',
            question: 'Confirm electrical load calculations and panel sizing',
            priority: 'High',
            tradeConcerned: 'Electrical',
            affectsScope: true,
            notes: 'Critical for safety and code compliance',
          },
          {
            number: 2,
            category: 'Design',
            question: 'Clarify HVAC zoning and economizer requirements',
            priority: 'High',
            tradeConcerned: 'HVAC',
            affectsScope: true,
            notes: 'Affects equipment selection and installation',
          },
          {
            number: 3,
            category: 'Scope',
            question: 'Confirm spray foam R-value specification',
            priority: 'Medium',
            tradeConcerned: 'Insulation',
            affectsScope: true,
            notes: 'Impacts material cost and performance',
          },
          {
            number: 4,
            category: 'Schedule',
            question: 'Provide access schedule for rooftop equipment installation',
            priority: 'High',
            tradeConcerned: 'HVAC',
            affectsScope: false,
            notes: 'Need crane coordination',
          },
        ],
      };

      setRFI(rfiData);
      setCurrentPage('rfi');
    } catch (error) {
      console.error('Error generating RFI:', error);
    }
  };

  const handleExportPDF = () => {
    alert('PDF export coming soon');
  };

  const handleBackToDashboard = () => {
    setCurrentPage('dashboard');
    setEstimate(null);
    setRFI(null);
  };

  const planLabel = planLimits[currentPlan]?.label || 'Free';
  const planColor = currentPlan === 'business' ? '#a78bfa' : currentPlan === 'pro' ? '#22c55e' : '#64748b';

  return (
    <div className="app">
      {/* Checkout result banner */}
      {checkoutBanner && (
        <div style={{
          background: checkoutBanner.type === 'success' ? 'rgba(34,197,94,0.12)' : 'rgba(234,179,8,0.12)',
          borderBottom: `1px solid ${checkoutBanner.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.3)'}`,
          color: checkoutBanner.type === 'success' ? '#4ade80' : '#fbbf24',
          padding: '10px 32px',
          fontSize: '13px',
          fontWeight: '600',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {checkoutBanner.msg}
          <button
            onClick={() => setCheckoutBanner(null)}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '16px' }}
          >
            ×
          </button>
        </div>
      )}

      <nav style={{
        background: '#0f172a',
        borderBottom: '1px solid #1e293b',
        padding: '0 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '56px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div
          onClick={handleBackToDashboard}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <div style={{
            background: '#dc2626',
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '800',
            color: 'white',
          }}>C</div>
          <span style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '16px', letterSpacing: '-0.3px' }}>
            ConstructPro
          </span>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleBackToDashboard}
            style={{
              background: currentPage === 'dashboard' ? 'rgba(220,38,38,0.15)' : 'transparent',
              color: currentPage === 'dashboard' ? '#f87171' : '#94a3b8',
              border: 'none',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Dashboard
          </button>

          {estimate && (
            <button
              onClick={() => setCurrentPage('estimate')}
              style={{
                background: currentPage === 'estimate' ? 'rgba(220,38,38,0.15)' : 'transparent',
                color: currentPage === 'estimate' ? '#f87171' : '#94a3b8',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Estimate
            </button>
          )}

          <button
            onClick={() => setCurrentPage('upload')}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '7px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              marginLeft: '8px',
            }}
          >
            New Estimate
          </button>

          {/* Plan badge */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid #334155',
            borderRadius: '6px',
            padding: '4px 10px',
            marginLeft: '4px',
          }}>
            <span style={{ color: planColor, fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {planLabel}
            </span>
          </div>

          {/* User menu */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: '700',
              color: 'white',
              cursor: 'pointer',
              marginLeft: '4px',
              title: user?.email,
            }}
            onClick={signOut}
            title={`${user?.email} — click to sign out`}
          >
            {(profile?.full_name || user?.email || 'U')[0].toUpperCase()}
          </div>
        </div>
      </nav>

      <div className="app-content">
        {currentPage === 'dashboard' && (
          <Dashboard onNewProject={() => setCurrentPage('upload')} />
        )}

        {currentPage === 'upload' && (
          <ProjectUpload onGenerateEstimate={handleGenerateEstimate} />
        )}

        {currentPage === 'estimate' && estimate && selectedProject && (
          <EstimateViewer
            estimate={estimate}
            project={selectedProject}
            onExportPDF={handleExportPDF}
          />
        )}

        {currentPage === 'estimate' && !estimate && (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>No estimate data available. Please create a new project.</p>
            <button className="btn btn-primary" onClick={handleBackToDashboard}>
              Back to Dashboard
            </button>
          </div>
        )}

        {currentPage === 'rfi' && rfi && (
          <RFIViewer
            rfi={rfi}
            project={selectedProject}
            onExportPDF={handleExportPDF}
            onBack={handleBackToDashboard}
          />
        )}
      </div>
    </div>
  );
}

// ─── Auth gate (login / signup) ───────────────────────────────────────────────
function AuthGate() {
  const { user, loading } = useAuth();
  const [authView, setAuthView] = useState('login');

  // Check URL for ?signup=1 or ?plan= to land on signup with a plan pre-selected
  const [defaultPlan, setDefaultPlan] = useState('pro');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === '1' || params.get('plan')) {
      setAuthView('signup');
      if (params.get('plan')) setDefaultPlan(params.get('plan'));
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ color: '#475569', fontSize: '14px' }}>Loading…</div>
      </div>
    );
  }

  // If Supabase not configured, skip auth entirely and show the app
  const supabaseConfigured = process.env.REACT_APP_SUPABASE_URL && 
    process.env.REACT_APP_SUPABASE_URL !== 'undefined';
  
  if (!supabaseConfigured || !user) {
    if (!supabaseConfigured) {
      return <AuthenticatedApp />;
    }
    if (authView === 'signup') {
      return (
        <SignupPage
          defaultPlan={defaultPlan}
          onSwitchToLogin={() => setAuthView('login')}
        />
      );
    }
    return (
      <LoginPage onSwitchToSignup={() => setAuthView('signup')} />
    );
  }

  return <AuthenticatedApp />;
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
